import { useState, useRef } from 'react';

export type DirectionStep = {
  sequence: number;
  instruction: string;
  image: File | null;
  imagePreview: string | null;
  saved: boolean;
};

interface Props {
  terminalName: string;
  initialSteps: DirectionStep[];
  onSave: (steps: DirectionStep[]) => void;
  onClose: () => void;
  saving?: boolean;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors';

export function DirectionStepsModal({ terminalName, initialSteps, onSave, onClose, saving }: Props) {
  const [steps, setSteps] = useState<DirectionStep[]>(initialSteps);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const fileRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const addStep = () => {
    const nextSeq = steps.length > 0 ? Math.max(...steps.map(s => s.sequence)) + 1 : 1;
    setSteps([...steps, { sequence: nextSeq, instruction: '', image: null, imagePreview: null, saved: false }]);
  };

  const updateStep = (idx: number, updates: Partial<DirectionStep>) => {
    setSteps(steps.map((s, i) => i === idx ? { ...s, ...updates } : s));
  };

  const removeStep = (idx: number) => {
    setSteps(steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sequence: i + 1 })));
  };

  const handleImage = (idx: number, file: File) => {
    const reader = new FileReader();
    reader.onload = () => updateStep(idx, { image: file, imagePreview: reader.result as string });
    reader.readAsDataURL(file);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-800">Directions — {terminalName}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Steps list */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {steps.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-8">No steps yet. Click "Add Step" to start.</p>
            )}
            {steps.map((step, idx) => (
              <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* Sequence badge */}
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                  {step.sequence}
                </div>

                {/* Image area */}
                <div className="flex-shrink-0">
                  {step.imagePreview ? (
                    <div className="relative group">
                      <img
                        src={step.imagePreview}
                        alt={`Step ${step.sequence}`}
                        className="w-48 h-36 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightbox(step.imagePreview)}
                      />
                      <button
                        onClick={() => updateStep(idx, { image: null, imagePreview: null })}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <label className="w-48 h-36 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                      <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                      </svg>
                      <span className="text-xs text-gray-400 mt-1">Upload Image</span>
                      <input
                        ref={el => { fileRefs.current[idx] = el; }}
                        type="file"
                        accept="image/*"
                        onChange={e => e.target.files?.[0] && handleImage(idx, e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Instruction + actions */}
                <div className="flex-1 flex flex-col gap-2">
                  <textarea
                    value={step.instruction}
                    onChange={e => updateStep(idx, { instruction: e.target.value })}
                    placeholder="Enter direction instruction..."
                    className={`${inputCls} resize-none flex-1 min-h-[80px]`}
                  />
                  <div className="flex justify-end">
                    <button onClick={() => removeStep(idx)} className="text-red-500 hover:text-red-700 text-xs font-medium cursor-pointer">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 flex items-center gap-3 flex-shrink-0">
            <button
              onClick={addStep}
              className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors cursor-pointer text-sm flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Step
            </button>
            <div className="flex-1" />
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors cursor-pointer">
              Cancel
            </button>
            <button
              onClick={() => onSave(steps)}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 cursor-pointer" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Full size" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </>
  );
}
